/**
 * ADR-0099 (V112-3-8, D-112-8): one scope model. The workspace profile's answers are the defaults of every system;
 * a system stores only the answers it set itself (`ai_systems.scope_overrides_json`, migration 761).
 *
 * This module is the one vocabulary for those answers and the one place that turns (profile defaults, a system's
 * stored classification, its overrides) into the effective answers the obligation engine reads. Pure: no I/O.
 */
import { AFFECTED_PERSONS } from '../vocabularies/affected-persons.js';
import { DATA_CLASSES, GDPR_SPECIAL_CATEGORIES } from '../vocabularies/data-classes.js';
/** One market list for profiles and systems (owner 2026-10-24: all 10 geographies). Engine upper-cases them. */
export const MARKETS = ['us', 'eu', 'uk', 'ca', 'cn', 'sg', 'jp', 'in', 'au', 'global'];
/** Data classes: the engine vocabulary plus the intake-only classes the old shims dropped. */
export const SCOPE_DATA_CLASSES = [...DATA_CLASSES, 'cui', 'de_identified', 'proprietary', 'public'];
/** Affected people: core AFFECTED_PERSONS (`workers` canonical; the employees ↔ workers shims retire). */
export const SCOPE_AFFECTED_PERSONS = AFFECTED_PERSONS;
/**
 * Decision types; each maps onto the engine's decision flags. `none` = no decision flags; `other` = a decision about
 * a person outside the three named kinds (housing, education, insurance…): consequential, no specific flag.
 */
export const DECISION_TYPES = ['none', 'employment', 'credit', 'healthcare', 'other'];
/** Profile industry → engine `sector` (intake values; no rule predicates on sector yet). */
export const INDUSTRY_SECTOR = {
    healthcare: 'healthcare', financial_services: 'finance', insurance: 'insurance', retail: 'retail',
    hr_recruiting: 'hr', legal: 'legal', media: 'media', saas: 'saas', government: 'government', education: 'education',
};
/** The inheritable answers (ADR-0099 §1). Override records name these keys. */
export const SCOPE_ANSWERS = ['markets', 'data_classes', 'affected_persons', 'decision_types', 'sector', 'deployer_type', 'federal_flowdown'];
export const isScopeAnswer = (v) => typeof v === 'string' && SCOPE_ANSWERS.includes(v);
/** Engine fields each answer writes (top level of the classification; readRaw prefers the top level). */
const DECISION_FLAGS = { employment: 'employment_decision', credit: 'credit_decision', healthcare: 'healthcare_decision' };
/** The engine fields a profile default contributes for one answer; `{}` when the profile doesn't answer it. */
export function defaultFields(answer, d) {
    switch (answer) {
        case 'markets': return d.geographies?.length ? { deployment_jurisdictions: [...d.geographies] } : {};
        case 'data_classes': return d.data_types?.length ? { data_classes: [...d.data_types] } : {};
        case 'affected_persons': return d.affected_persons?.length ? { affected_persons: [...d.affected_persons] } : {};
        case 'decision_types': {
            if (!d.decision_types?.length)
                return {};
            const t = new Set(d.decision_types.filter((x) => x !== 'none'));
            return {
                consequential_decision: t.size > 0,
                employment_decision: t.has('employment'), credit_decision: t.has('credit'), healthcare_decision: t.has('healthcare'),
            };
        }
        case 'sector': {
            const one = d.industry_codes?.length === 1 ? INDUSTRY_SECTOR[d.industry_codes[0]] : undefined;
            return one ? { sector: one } : {};
        }
        case 'deployer_type': return d.deployer_type ? { deployer_type: d.deployer_type } : {};
        case 'federal_flowdown': return d.federal_flowdown === null || d.federal_flowdown === undefined ? {} : { federal_flowdown: d.federal_flowdown };
    }
}
/** The engine fields that belong to an answer (removed before a default is written, so a stale copy can't win). */
export const ANSWER_FIELDS = {
    markets: ['deployment_jurisdictions'],
    data_classes: ['data_classes'],
    affected_persons: ['affected_persons'],
    decision_types: ['consequential_decision', ...Object.values(DECISION_FLAGS)],
    sector: ['sector'],
    deployer_type: ['deployer_type'],
    federal_flowdown: ['federal_flowdown'],
};
/**
 * The effective answers the engine reads. `overrides` = the answers the system set (null = a row from before 761,
 * read as today: every stored answer is the system's own, and only the federal answers fall back to the profile).
 * An inherited answer drops the system's stored copy (top level and the deployment_context mirror) and takes the
 * profile default. When the profile doesn't answer it, the stored copy stays: after the 761 backfill an inherited
 * answer's stored value equals "not answered" (absent, [] or false), so keeping it changes no rule and keeps the
 * obligation-set hash stable (golden tests).
 */
export function effectiveScope(classification, defaults, overrides) {
    const out = { ...classification };
    const ctx = classification.deployment_context && typeof classification.deployment_context === 'object'
        ? { ...classification.deployment_context } : null;
    const own = (a) => {
        if (overrides)
            return overrides.includes(a);
        // Legacy row: an answer the classification carries is the system's own.
        return ANSWER_FIELDS[a].some((f) => out[f] !== undefined || (ctx && ctx[f] !== undefined));
    };
    for (const a of SCOPE_ANSWERS) {
        if (own(a))
            continue;
        if (!overrides && a !== 'deployer_type' && a !== 'federal_flowdown')
            continue; // legacy: only the federal answers inherit
        const d = defaultFields(a, defaults);
        if (Object.keys(d).length === 0)
            continue; // not answered on the profile: the stored copy stands (see above)
        for (const f of ANSWER_FIELDS[a]) {
            delete out[f];
            if (ctx)
                delete ctx[f];
        }
        Object.assign(out, d);
    }
    if (ctx)
        out.deployment_context = ctx;
    return out;
}
/** The overrides after a system saves `answers` (each set answer becomes the system's own). */
export const withOverrides = (current, answers) => [...new Set([...(current ?? []).filter(isScopeAnswer), ...answers])].sort();
/** Which answers a classification PATCH body sets (engine field names → answers). */
export function answersInBody(body) {
    return SCOPE_ANSWERS.filter((a) => ANSWER_FIELDS[a].some((f) => body[f] !== undefined));
}
/**
 * The record a system has, for a save: its stored one, or for a row without one (written before 761 or by a path
 * that doesn't keep a record) the legacy reading — every answer its stored classification carries is its own.
 */
export function overridesOf(stored, classification) {
    if (stored)
        return stored.filter(isScopeAnswer);
    const ctx = classification.deployment_context && typeof classification.deployment_context === 'object'
        ? classification.deployment_context : {};
    return SCOPE_ANSWERS.filter((a) => ANSWER_FIELDS[a].some((f) => classification[f] !== undefined || ctx[f] !== undefined));
}
/** "Reset to workspace": drop the answers' stored values (top level and deployment_context) from a classification. */
export function withoutAnswers(classification, answers) {
    const out = { ...classification };
    const ctx = out.deployment_context && typeof out.deployment_context === 'object' ? { ...out.deployment_context } : null;
    for (const a of answers)
        for (const f of ANSWER_FIELDS[a]) {
            delete out[f];
            if (ctx)
                delete ctx[f];
        }
    if (ctx)
        out.deployment_context = ctx;
    return out;
}
/**
 * Owner D2 (2026-10-25, ADR-0099 amendment): one system-only question, "Who has the final say?", writes the three
 * engine fields together so they can't contradict each other. `autonomy_level` keeps the values rules read.
 */
export const FINAL_SAY = {
    informs: { autonomy_level: 'informational', decision_role: 'informs', human_oversight_mode: 'none' },
    suggests: { autonomy_level: 'decision_support', decision_role: 'recommends', human_oversight_mode: 'review_before_effect' },
    approves: { autonomy_level: 'human_in_the_loop', decision_role: 'decides', human_oversight_mode: 'review_before_effect' },
    monitors: { autonomy_level: 'human_on_the_loop', decision_role: 'decides', human_oversight_mode: 'continuous_monitoring' },
    automatic: { autonomy_level: 'fully_automated', decision_role: 'decides', human_oversight_mode: 'appeal_after_effect' },
};
export const FINAL_SAY_OPTIONS = Object.keys(FINAL_SAY);
/** The final-say answer a stored classification reads as (its autonomy level), or null. */
export function finalSayOf(classification) {
    const a = classification.autonomy_level;
    return FINAL_SAY_OPTIONS.find((k) => FINAL_SAY[k].autonomy_level === a) ?? null;
}
/**
 * Owner D1 (2026-10-25): Data categories and Affected parties are no longer asked; the engine derives the one
 * category a rule reads. Any GDPR special category (or PHI) among the data classes means `sensitive`. Additive:
 * a stored category stays (the screen no longer writes them).
 */
export function withDerivedCategories(classification) {
    const classes = Array.isArray(classification.data_classes) ? classification.data_classes : [];
    const special = classes.some((c) => c === 'phi' || GDPR_SPECIAL_CATEGORIES.includes(c));
    if (!special)
        return classification;
    const cats = Array.isArray(classification.data_categories) ? classification.data_categories.filter((x) => typeof x === 'string') : [];
    return cats.includes('sensitive') ? classification : { ...classification, data_categories: [...cats, 'sensitive'] };
}
/**
 * Owner D4 (2026-10-25, extended after the round-2 review): the system-only answers carry the same who / when / why
 * stamp as the inheritable ones; they decide duties too (the final say drives GDPR Art. 22). Keys = engine fields.
 */
export const SYSTEM_ANSWER_FIELDS = {
    final_say: ['autonomy_level', 'decision_role', 'human_oversight_mode'],
    security_levels: ['data_classification_json'],
    exposure: ['exposure_level'],
    deployment_target: ['deployment_target'],
    output_destinations: ['output_destinations'],
    embedded_third_party_ai: ['embedded_third_party_ai'],
    affected_person_categories: ['affected_person_categories'],
    gpai_role: ['gpai_role'],
};
/** Which system-only answers a classification PATCH body sets. */
export function systemAnswersInBody(body) {
    return Object.keys(SYSTEM_ANSWER_FIELDS)
        .filter((a) => SYSTEM_ANSWER_FIELDS[a].some((f) => body[f] !== undefined));
}
/** The attribution record after a save: set answers get this save's who / when / reason; reset answers drop theirs. */
export function withOverrideMeta(current, set, reset, who) {
    const out = { ...(current ?? {}) };
    const entry = { by: who.by, by_name: who.by_name, at: who.at, ...(who.reason ? { reason: who.reason } : {}) };
    for (const a of set)
        out[a] = entry;
    for (const a of reset)
        delete out[a];
    return out;
}
/** Owner D5: the sector a default gave that a change takes away (one industry → several), or null. */
export function sectorLostBy(prior, next) {
    const before = defaultFields('sector', prior).sector;
    return typeof before === 'string' && defaultFields('sector', next).sector === undefined ? before : null;
}
//# sourceMappingURL=scope-model.js.map